// assignEngine.js

import { buildOptionValueSet } from "../fields/FieldOptionHelpers";

const ASSIGN_ENGINE_DEBUG = true;

function logAssignEngine(event, payload) {
  if (!ASSIGN_ENGINE_DEBUG) return;
  try {
    console.log(`[assignEngine] ${event}`, payload);
  } catch (error) {
    console.log(`[assignEngine] ${event}`);
  }
}

function getProcessedFieldSummary(processedFields) {
  return (Array.isArray(processedFields) ? processedFields : []).map((field) => ({
    key: field?.key != null ? String(field.key) : "",
    field: field?.field != null ? String(field.field) : "",
    dataSource: field?.dataSource != null ? String(field.dataSource) : "",
    fieldType: field?.fieldType != null ? String(field.fieldType) : "",
    headerName: field?.headerName != null ? String(field.headerName) : "",
  }));
}

function getFieldConfigSummary(fieldConfig) {
  if (!fieldConfig || typeof fieldConfig !== "object") return null;

  return {
    key: fieldConfig.key != null ? String(fieldConfig.key) : "",
    field: fieldConfig.field != null ? String(fieldConfig.field) : "",
    dataSource: fieldConfig.dataSource != null ? String(fieldConfig.dataSource) : "",
    fieldType: fieldConfig.fieldType != null ? String(fieldConfig.fieldType) : "",
    headerName: fieldConfig.headerName != null ? String(fieldConfig.headerName) : "",
  };
}

function getSafeObjectKeys(value) {
  if (!value || typeof value !== "object") return [];
  try {
    return Object.keys(value).sort();
  } catch (error) {
    return [];
  }
}

function getSafeObjectPreview(value) {
  if (!value || typeof value !== "object") return value;
  try {
    return value;
  } catch (error) {
    return "[uninspectable object]";
  }
}

function buildNeedStatusSummary({
  submission,
  reviewers,
  reviews,
  config,
  viewfieldsInstance,
  processedFields,
  submissionFieldConfig,
  reviewerFieldConfig,
  rawSubmissionAoEValue,
  submissionAoE,
  reviewerInspectionRows,
}) {
  const safeReviewers = Array.isArray(reviewers) ? reviewers : [];
  const safeReviews = Array.isArray(reviews) ? reviews : [];
  const eligibleReviewers = safeReviewers.filter((reviewer) => reviewer?.reviewer === true);
  const reviewersWithIds = safeReviewers.filter((reviewer) => {
    if (reviewer?.id != null) return true;
    if (reviewer?.userId != null) return true;
    return false;
  });

  const reviewersWithRawAoE = reviewerInspectionRows.filter(
    (row) => row.rawReviewerAoEValue !== undefined && row.rawReviewerAoEValue !== null && row.rawReviewerAoEValue !== ""
  );

  const reviewersWithNormalizedAoE = reviewerInspectionRows.filter(
    (row) => Array.isArray(row.normalizedReviewerAoE) && row.normalizedReviewerAoE.length > 0
  );

  return {
    haveSubmission: !!submission,
    haveSubmissionId: !!getSubmissionId(submission),
    haveReviewers: safeReviewers.length > 0,
    haveReviews: safeReviews.length > 0,
    haveConfig: !!config,
    haveViewfieldsInstance: !!viewfieldsInstance,
    haveProcessedFields: Array.isArray(processedFields) && processedFields.length > 0,
    haveSubmissionFieldConfig: !!submissionFieldConfig,
    haveReviewerFieldConfig: !!reviewerFieldConfig,
    haveSubmissionRawAoE:
      rawSubmissionAoEValue !== undefined && rawSubmissionAoEValue !== null && rawSubmissionAoEValue !== "",
    haveSubmissionNormalizedAoE: Array.isArray(submissionAoE) && submissionAoE.length > 0,
    haveReviewersWithIds: reviewersWithIds.length > 0,
    haveEligibleReviewers: eligibleReviewers.length > 0,
    haveAnyReviewerRawAoE: reviewersWithRawAoE.length > 0,
    haveAnyReviewerNormalizedAoE: reviewersWithNormalizedAoE.length > 0,
    counts: {
      reviewerCount: safeReviewers.length,
      reviewCount: safeReviews.length,
      eligibleReviewerCount: eligibleReviewers.length,
      reviewersWithIdsCount: reviewersWithIds.length,
      reviewersWithRawAoECount: reviewersWithRawAoE.length,
      reviewersWithNormalizedAoECount: reviewersWithNormalizedAoE.length,
    },
  };
}

function normalizeNullableLimit(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  if (n <= 0) return null;
  return n;
}

function normalizeStringArray(value, multiple = true) {
  if (value == null) return [];
  if (multiple) {
    if (Array.isArray(value)) {
      return value
        .map((item) => (item == null ? "" : String(item).trim()))
        .filter(Boolean);
    }
    return [String(value).trim()].filter(Boolean);
  }
  return [String(value).trim()].filter(Boolean);
}

function uniqueStrings(values) {
  return Array.from(new Set((values || []).filter(Boolean)));
}

function toKey(value) {
  return value == null ? "" : String(value);
}

function getFieldLikeKey(fieldConfig) {
  if (!fieldConfig || typeof fieldConfig !== "object") return "";
  if (fieldConfig.field != null) return String(fieldConfig.field);
  if (fieldConfig.key != null) return String(fieldConfig.key);
  return "";
}

function findFieldConfig(processedFields, targetKey) {
  const wanted = toKey(targetKey);
  if (!wanted || !Array.isArray(processedFields)) return null;

  return (
    processedFields.find((field) => {
      const fieldKey = getFieldLikeKey(field);
      return fieldKey === wanted;
    }) || null
  );
}

function getNestedPropertyValue(object, path) {
  if (!object || typeof object !== "object" || !path || typeof path !== "string") return undefined;

  return path.split(".").reduce((acc, key) => {
    if (acc && typeof acc === "object") return acc[key];
    return undefined;
  }, object);
}

function getObjectValueByFieldConfig(record, fieldConfig) {
  if (!record || !fieldConfig) return undefined;

  if (fieldConfig.dataSource) {
    return getNestedPropertyValue(record, fieldConfig.dataSource);
  }

  const directFieldKey = getFieldLikeKey(fieldConfig);
  if (directFieldKey && record[directFieldKey] !== undefined) {
    return record[directFieldKey];
  }

  return undefined;
}

function getObjectValueForAssignmentField(record, fieldConfig, assignmentFieldConfig) {
  if (!record) return undefined;

  if (assignmentFieldConfig?.dataSource) {
    return getNestedPropertyValue(record, assignmentFieldConfig.dataSource);
  }

  return getObjectValueByFieldConfig(record, fieldConfig);
}

function getSubmissionId(submission) {
  return submission?.id != null ? String(submission.id) : "";
}

function getReviewerId(reviewer) {
  if (reviewer?.id != null) return String(reviewer.id);
  if (reviewer?.userId != null) return String(reviewer.userId);
  return "";
}

function getReviewId(review) {
  return review?.id != null ? String(review.id) : "";
}

function getReviewReviewerId(review) {
  if (review?.userId != null) return String(review.userId);
  if (review?.meta?.owner?.userId != null) return String(review.meta.owner.userId);
  return "";
}

function defaultGetLinkedSubmissionId(review) {
  const linkedData = Array.isArray(review?.linkedData) ? review.linkedData : [];

  for (const item of linkedData) {
    if (!item || typeof item !== "object") continue;

    const modelType = String(item.modelType ?? item.model ?? "").toUpperCase();

    if (item.linkedTo != null && String(item.linkedTo).trim() && modelType === "SUBMISSION") {
      return String(item.linkedTo);
    }
  }

  return "";
}

function buildReviewIndexes(reviews, getLinkedSubmissionId) {
  const reviewsBySubmissionId = new Map();
  const reviewsByReviewerId = new Map();
  const reviewPairs = new Set();

  for (const review of Array.isArray(reviews) ? reviews : []) {
    const reviewId = getReviewId(review);
    const reviewerId = getReviewReviewerId(review);
    const submissionId = getLinkedSubmissionId(review);

    if (submissionId) {
      if (!reviewsBySubmissionId.has(submissionId)) {
        reviewsBySubmissionId.set(submissionId, []);
      }
      reviewsBySubmissionId.get(submissionId).push(review);
    }

    if (reviewerId) {
      if (!reviewsByReviewerId.has(reviewerId)) {
        reviewsByReviewerId.set(reviewerId, []);
      }
      reviewsByReviewerId.get(reviewerId).push(review);
    }

    if (submissionId && reviewerId) {
      reviewPairs.add(`${submissionId}::${reviewerId}`);
    }

    if (!reviewId) {
      continue;
    }
  }

  return {
    reviewsBySubmissionId,
    reviewsByReviewerId,
    reviewPairs,
  };
}

function getAutoAssignmentConfig(config) {
  return config?.auto_assignment || {};
}

function getReviewRules(config) {
  return config?.reviews || {};
}

function getLimits(config) {
  const reviewRules = getReviewRules(config);

  return {
    minimumPerSubmission: normalizeNullableLimit(reviewRules.minimum_per_submission),
    maximumPerSubmission: normalizeNullableLimit(reviewRules.maximum_per_submission),
    minimumPerReviewer: normalizeNullableLimit(reviewRules.minimum_per_reviewer),
    maximumPerReviewer: normalizeNullableLimit(reviewRules.maximum_per_reviewer),
    allowSelfAssignment: reviewRules.allow_self_assignment === true,
  };
}

function resolveFieldConfigs(viewfieldsInstance, autoAssignmentConfig, processedFieldsOverride) {
  const processedFields =
    processedFieldsOverride ||
    viewfieldsInstance?.getProcessedFields?.({ requireBuilt: true }) ||
    [];

  const submissionFieldConfig = findFieldConfig(
    processedFields,
    autoAssignmentConfig?.submission_field?.key
  );

  const reviewerFieldConfig = findFieldConfig(
    processedFields,
    autoAssignmentConfig?.reviewer_field?.key
  );

  logAssignEngine("resolveFieldConfigs", {
    requestedSubmissionFieldKey: autoAssignmentConfig?.submission_field?.key || "",
    requestedReviewerFieldKey: autoAssignmentConfig?.reviewer_field?.key || "",
    hasViewfieldsInstance: !!viewfieldsInstance,
    processedFieldCount: Array.isArray(processedFields) ? processedFields.length : 0,
    availableProcessedFields: getProcessedFieldSummary(processedFields),
    submissionFieldFound: !!submissionFieldConfig,
    reviewerFieldFound: !!reviewerFieldConfig,
    submissionFieldConfig: getFieldConfigSummary(submissionFieldConfig),
    reviewerFieldConfig: getFieldConfigSummary(reviewerFieldConfig),
  });

  return {
    processedFields,
    submissionFieldConfig,
    reviewerFieldConfig,
  };
}

function resolveFieldOptions(viewfieldsInstance, fieldConfig) {
  if (!viewfieldsInstance || !fieldConfig) {
    return { flatOptions: [], treeOptions: [] };
  }

  const resolved = viewfieldsInstance.resolveOptionsForFieldExternal?.(fieldConfig);
  return {
    flatOptions: Array.isArray(resolved?.flatOptions) ? resolved.flatOptions : [],
    treeOptions: Array.isArray(resolved?.treeOptions) ? resolved.treeOptions : [],
  };
}

function extractSubmissionAoE(submission, submissionFieldConfig, autoAssignmentConfig) {
  const rawValue = getObjectValueForAssignmentField(
    submission,
    submissionFieldConfig,
    autoAssignmentConfig?.submission_field
  );
  const multiple = autoAssignmentConfig?.submission_field?.multiple === true;
  return uniqueStrings(normalizeStringArray(rawValue, multiple));
}

function extractReviewerAoE(reviewer, reviewerFieldConfig, autoAssignmentConfig) {
  const rawValue = getObjectValueForAssignmentField(
    reviewer,
    reviewerFieldConfig,
    autoAssignmentConfig?.reviewer_field
  );
  const multiple = autoAssignmentConfig?.reviewer_field?.multiple !== false;
  return uniqueStrings(normalizeStringArray(rawValue, multiple));
}

function getOverlap(submissionAoE, reviewerAoE) {
  const reviewerSet = new Set(reviewerAoE);
  return submissionAoE.filter((value) => reviewerSet.has(value));
}

function calculateSuitabilityPercentage(overlapCount, submissionAoECount) {
  if (!submissionAoECount || submissionAoECount <= 0) return 0;
  return Math.round((overlapCount / submissionAoECount) * 100);
}

function buildReviewerContext(reviewerId, indexes, limits) {
  const currentReviews = indexes.reviewsByReviewerId.get(reviewerId) || [];
  const currentReviewCount = currentReviews.length;
  const maxReached = limits.maximumPerReviewer != null && currentReviewCount >= limits.maximumPerReviewer;

  return {
    currentReviewCount,
    maxReached,
  };
}

function buildSubmissionContext(submissionId, indexes, limits) {
  const currentReviews = indexes.reviewsBySubmissionId.get(submissionId) || [];
  const currentReviewCount = currentReviews.length;
  const maxReached = limits.maximumPerSubmission != null && currentReviewCount >= limits.maximumPerSubmission;

  return {
    currentReviewCount,
    maxReached,
  };
}

function compareCandidateRank(a, b, autoAssignmentConfig) {
  if (b.overlapCount !== a.overlapCount) return b.overlapCount - a.overlapCount;
  if (b.suitabilityPercentage !== a.suitabilityPercentage) {
    return b.suitabilityPercentage - a.suitabilityPercentage;
  }

  if (autoAssignmentConfig?.prefer_narrower_reviewer_aoe_when_scores_equal) {
    if (a.reviewerAoECount !== b.reviewerAoECount) {
      return a.reviewerAoECount - b.reviewerAoECount;
    }
  }

  if (a.currentReviewCount !== b.currentReviewCount) {
    return a.currentReviewCount - b.currentReviewCount;
  }

  return a.reviewerId.localeCompare(b.reviewerId);
}

function buildCommonContext({
  submissions,
  reviewers,
  reviews,
  config,
  viewfieldsInstance,
  processedFields,
  getLinkedSubmissionId,
}) {
  const autoAssignmentConfig = getAutoAssignmentConfig(config);
  const limits = getLimits(config);

  const fieldResolution = resolveFieldConfigs(
    viewfieldsInstance,
    autoAssignmentConfig,
    processedFields
  );

  const submissionFieldConfig = fieldResolution.submissionFieldConfig;
  const reviewerFieldConfig = fieldResolution.reviewerFieldConfig;

  logAssignEngine("buildCommonContext:start", {
    submissionCount: Array.isArray(submissions) ? submissions.length : 0,
    reviewerCount: Array.isArray(reviewers) ? reviewers.length : 0,
    reviewCount: Array.isArray(reviews) ? reviews.length : 0,
    autoAssignmentConfig: {
      submissionFieldKey: autoAssignmentConfig?.submission_field?.key || "",
      submissionFieldDataSource: autoAssignmentConfig?.submission_field?.dataSource || "",
      submissionFieldMultiple: autoAssignmentConfig?.submission_field?.multiple === true,
      reviewerFieldKey: autoAssignmentConfig?.reviewer_field?.key || "",
      reviewerFieldDataSource: autoAssignmentConfig?.reviewer_field?.dataSource || "",
      reviewerFieldMultiple: autoAssignmentConfig?.reviewer_field?.multiple !== false,
      minimumOverlapRequired: autoAssignmentConfig?.minimum_overlap_required,
      preferNarrowerReviewerAoEWhenScoresEqual:
        autoAssignmentConfig?.prefer_narrower_reviewer_aoe_when_scores_equal === true,
    },
    limits,
    hasViewfieldsInstance: !!viewfieldsInstance,
    processedFieldCount: Array.isArray(fieldResolution.processedFields)
      ? fieldResolution.processedFields.length
      : 0,
    submissionFieldConfig: getFieldConfigSummary(submissionFieldConfig),
    reviewerFieldConfig: getFieldConfigSummary(reviewerFieldConfig),
  });

  if (!submissionFieldConfig) {
    logAssignEngine("buildCommonContext:error", {
      error: "Submission field config not found.",
      requestedSubmissionFieldKey: autoAssignmentConfig?.submission_field?.key || "",
      availableProcessedFields: getProcessedFieldSummary(fieldResolution.processedFields),
    });

    throw new Error(
      `[assignEngine] Submission field config not found for key "${autoAssignmentConfig?.submission_field?.key || ""}".`
    );
  }

  if (!reviewerFieldConfig) {
    logAssignEngine("buildCommonContext:error", {
      error: "Reviewer field config not found.",
      requestedReviewerFieldKey: autoAssignmentConfig?.reviewer_field?.key || "",
      availableProcessedFields: getProcessedFieldSummary(fieldResolution.processedFields),
    });

    throw new Error(
      `[assignEngine] Reviewer field config not found for key "${autoAssignmentConfig?.reviewer_field?.key || ""}".`
    );
  }

  const submissionOptions = resolveFieldOptions(viewfieldsInstance, submissionFieldConfig);
  const reviewerOptions = resolveFieldOptions(viewfieldsInstance, reviewerFieldConfig);

  const submissionOptionValues = buildOptionValueSet(submissionOptions);
  const reviewerOptionValues = buildOptionValueSet(reviewerOptions);

  const indexes = buildReviewIndexes(reviews, getLinkedSubmissionId || defaultGetLinkedSubmissionId);

  logAssignEngine("buildCommonContext:resolved", {
    submissionOptionCount: submissionOptions.flatOptions.length,
    reviewerOptionCount: reviewerOptions.flatOptions.length,
    submissionOptionValues: Array.from(submissionOptionValues),
    reviewerOptionValues: Array.from(reviewerOptionValues),
    indexedSubmissionReviewGroups: indexes.reviewsBySubmissionId.size,
    indexedReviewerReviewGroups: indexes.reviewsByReviewerId.size,
    indexedReviewPairs: indexes.reviewPairs.size,
  });

  return {
    submissions: Array.isArray(submissions) ? submissions : [],
    reviewers: Array.isArray(reviewers) ? reviewers : [],
    reviews: Array.isArray(reviews) ? reviews : [],
    config,
    autoAssignmentConfig,
    limits,
    fieldResolution,
    submissionFieldConfig,
    reviewerFieldConfig,
    submissionOptions,
    reviewerOptions,
    submissionOptionValues,
    reviewerOptionValues,
    indexes,
  };
}

function buildRecommendationWarnings({
  overlapCount,
  reviewerAoECount,
  reviewerContext,
  submissionContext,
  limits,
}) {
  const warnings = [];

  if (overlapCount <= 0) {
    warnings.push("No matching area of expertise.");
  }

  if (reviewerContext.maxReached) {
    warnings.push("Reviewer maximum reached.");
  }

  if (submissionContext.maxReached) {
    warnings.push("Submission maximum reached.");
  }

  if (limits.maximumPerReviewer == null && reviewerAoECount > 1) {
    warnings.push("Reviewer has multiple areas of expertise; keep broader expertise available where possible.");
  }

  return warnings;
}

function buildCandidate({
  submission,
  reviewer,
  submissionAoE,
  reviewerAoE,
  overlap,
  indexes,
  limits,
}) {
  const submissionId = getSubmissionId(submission);
  const reviewerId = getReviewerId(reviewer);

  const reviewerContext = buildReviewerContext(reviewerId, indexes, limits);
  const submissionContext = buildSubmissionContext(submissionId, indexes, limits);

  return {
    reviewerId,
    reviewer,
    submissionId,
    overlapValues: overlap,
    overlapCount: overlap.length,
    submissionAoE,
    reviewerAoE,
    reviewerAoECount: reviewerAoE.length,
    suitabilityPercentage: calculateSuitabilityPercentage(overlap.length, submissionAoE.length),
    currentReviewCount: reviewerContext.currentReviewCount,
    reviewerContext,
    submissionContext,
    warnings: buildRecommendationWarnings({
      overlapCount: overlap.length,
      reviewerAoECount: reviewerAoE.length,
      reviewerContext,
      submissionContext,
      limits,
    }),
  };
}

function isReviewerEligible(reviewer) {
  return reviewer?.reviewer === true;
}

function isDuplicateAssignment(submissionId, reviewerId, indexes) {
  return indexes.reviewPairs.has(`${submissionId}::${reviewerId}`);
}

function recommendForSubmission({
  submission,
  reviewers,
  reviews,
  config,
  viewfieldsInstance,
  processedFields,
  getLinkedSubmissionId,
  includeInvalidCandidates = false,
}) {
  logAssignEngine("recommendForSubmission:ENTERED", {
    submissionId: getSubmissionId(submission),
    reviewerCount: Array.isArray(reviewers) ? reviewers.length : 0,
    reviewCount: Array.isArray(reviews) ? reviews.length : 0,
    hasConfig: !!config,
    hasViewfieldsInstance: !!viewfieldsInstance,
    processedFieldCount: Array.isArray(processedFields) ? processedFields.length : null,
    includeInvalidCandidates,
  });

  logAssignEngine("recommendForSubmission:reviewerSamples", {
    reviewerSamples: (Array.isArray(reviewers) ? reviewers : []).slice(0, 3).map((reviewer) => ({
      reviewerId: getReviewerId(reviewer),
      reviewerFlag: reviewer?.reviewer === true,
      keys: getSafeObjectKeys(reviewer),
      reviewerObject: getSafeObjectPreview(reviewer),
    })),
  });

  const common = buildCommonContext({
    submissions: [submission],
    reviewers,
    reviews,
    config,
    viewfieldsInstance,
    processedFields,
    getLinkedSubmissionId,
  });

  const submissionId = getSubmissionId(submission);
  const rawSubmissionAoEValue = getObjectValueForAssignmentField(
    submission,
    common.submissionFieldConfig,
    common.autoAssignmentConfig?.submission_field
  );
  const submissionAoE = extractSubmissionAoE(
    submission,
    common.submissionFieldConfig,
    common.autoAssignmentConfig
  );

  const minimumOverlapRequired = Number(common.autoAssignmentConfig?.minimum_overlap_required || 1);

  logAssignEngine("recommendForSubmission:submissionData", {
    submissionId,
    submissionKeys: getSafeObjectKeys(submission),
    submissionFieldConfig: getFieldConfigSummary(common.submissionFieldConfig),
    submissionFieldAssignmentConfig: common.autoAssignmentConfig?.submission_field || null,
    rawSubmissionAoEValue,
    normalizedSubmissionAoE: submissionAoE,
    minimumOverlapRequired,
    currentSubmissionReviewCount: buildSubmissionContext(
      submissionId,
      common.indexes,
      common.limits
    ).currentReviewCount,
  });

  const validCandidates = [];
  const invalidCandidates = [];
  const reviewerInspectionRows = [];

  for (const reviewer of common.reviewers) {
    const reviewerId = getReviewerId(reviewer);

    if (!reviewerId) {
      const skippedRow = {
        reviewerId: "",
        reviewerEligible: reviewer?.reviewer === true,
        reviewerKeys: getSafeObjectKeys(reviewer),
        rawReviewerAoEValue: undefined,
        normalizedReviewerAoE: [],
        overlapValues: [],
        overlapCount: 0,
        duplicateAssignment: false,
        currentReviewerReviewCount: 0,
        reviewerMaxReached: false,
        submissionMaxReached: false,
        valid: false,
        reasons: ["Reviewer has no id or userId."],
      };

      reviewerInspectionRows.push(skippedRow);

      logAssignEngine("recommendForSubmission:reviewerSkipped", {
        reason: "Reviewer has no id or userId.",
        reviewerKeys: getSafeObjectKeys(reviewer),
        reviewerObject: getSafeObjectPreview(reviewer),
      });

      continue;
    }

    const rawReviewerAoEValue = getObjectValueForAssignmentField(
      reviewer,
      common.reviewerFieldConfig,
      common.autoAssignmentConfig?.reviewer_field
    );
    const reviewerAoE = extractReviewerAoE(
      reviewer,
      common.reviewerFieldConfig,
      common.autoAssignmentConfig
    );

    const overlap = getOverlap(submissionAoE, reviewerAoE);
    const candidate = buildCandidate({
      submission,
      reviewer,
      submissionAoE,
      reviewerAoE,
      overlap,
      indexes: common.indexes,
      limits: common.limits,
    });

    const duplicateAssignment = isDuplicateAssignment(submissionId, reviewerId, common.indexes);
    const reasons = [];

    if (!isReviewerEligible(reviewer)) {
      reasons.push("User is not marked as a reviewer.");
    }

    if (!submissionId) {
      reasons.push("Submission has no id.");
    }

    if (submissionAoE.length === 0) {
      reasons.push("Submission has no assignment areas.");
    }

    if (reviewerAoE.length === 0) {
      reasons.push("Reviewer has no specialist areas.");
    }

    if (overlap.length < minimumOverlapRequired) {
      reasons.push("Reviewer does not meet the minimum overlap requirement.");
    }

    if (candidate.reviewerContext.maxReached) {
      reasons.push("Reviewer maximum reached.");
    }

    if (candidate.submissionContext.maxReached) {
      reasons.push("Submission maximum reached.");
    }

    if (duplicateAssignment) {
      reasons.push("Reviewer is already assigned to this submission.");
    }

    const result = {
      ...candidate,
      valid: reasons.length === 0,
      reasons,
    };

    const inspectionRow = {
      reviewerId,
      reviewerEligible: isReviewerEligible(reviewer),
      reviewerKeys: getSafeObjectKeys(reviewer),
      reviewerFieldConfig: getFieldConfigSummary(common.reviewerFieldConfig),
      reviewerFieldAssignmentConfig: common.autoAssignmentConfig?.reviewer_field || null,
      rawReviewerAoEValue,
      normalizedReviewerAoE: reviewerAoE,
      overlapValues: overlap,
      overlapCount: overlap.length,
      duplicateAssignment,
      currentReviewerReviewCount: candidate.reviewerContext.currentReviewCount,
      reviewerMaxReached: candidate.reviewerContext.maxReached,
      submissionMaxReached: candidate.submissionContext.maxReached,
      valid: result.valid,
      reasons: result.reasons,
    };

    reviewerInspectionRows.push(inspectionRow);

    logAssignEngine("recommendForSubmission:reviewerCheck", inspectionRow);

    if (result.valid) {
      validCandidates.push(result);
    } else {
      invalidCandidates.push(result);
    }
  }

  validCandidates.sort((a, b) => compareCandidateRank(a, b, common.autoAssignmentConfig));
  invalidCandidates.sort((a, b) => compareCandidateRank(a, b, common.autoAssignmentConfig));

  const needStatusSummary = buildNeedStatusSummary({
    submission,
    reviewers: common.reviewers,
    reviews: common.reviews,
    config,
    viewfieldsInstance,
    processedFields: common.fieldResolution?.processedFields,
    submissionFieldConfig: common.submissionFieldConfig,
    reviewerFieldConfig: common.reviewerFieldConfig,
    rawSubmissionAoEValue,
    submissionAoE,
    reviewerInspectionRows,
  });

  logAssignEngine("recommendForSubmission:needStatus", needStatusSummary);

  logAssignEngine("recommendForSubmission:summary", {
    submissionId,
    submissionAoE,
    reviewerCount: common.reviewers.length,
    eligibleReviewerCount: common.reviewers.filter((reviewer) => isReviewerEligible(reviewer)).length,
    validCandidateCount: validCandidates.length,
    invalidCandidateCount: invalidCandidates.length,
    validCandidateReviewerIds: validCandidates.map((candidate) => candidate.reviewerId),
    invalidCandidateSummary: invalidCandidates.map((candidate) => ({
      reviewerId: candidate.reviewerId,
      reasons: candidate.reasons,
    })),
  });

  return {
    submissionId,
    submission,
    submissionAoE,
    validCandidates,
    invalidCandidates: includeInvalidCandidates ? invalidCandidates : [],
    currentSubmissionReviewCount: buildSubmissionContext(submissionId, common.indexes, common.limits).currentReviewCount,
    limits: common.limits,
  };
}

function recommendForSubmissions({
  submissions,
  reviewers,
  reviews,
  config,
  viewfieldsInstance,
  processedFields,
  getLinkedSubmissionId,
  includeInvalidCandidates = false,
}) {
  logAssignEngine("recommendForSubmissions:ENTERED", {
    submissionCount: Array.isArray(submissions) ? submissions.length : 0,
    reviewerCount: Array.isArray(reviewers) ? reviewers.length : 0,
    reviewCount: Array.isArray(reviews) ? reviews.length : 0,
    includeInvalidCandidates,
  });

  return (Array.isArray(submissions) ? submissions : []).map((submission) =>
    recommendForSubmission({
      submission,
      reviewers,
      reviews,
      config,
      viewfieldsInstance,
      processedFields,
      getLinkedSubmissionId,
      includeInvalidCandidates,
    })
  );
}

function comparePlanSubmissionPriority(a, b) {
  if (a.missingToMinimum !== b.missingToMinimum) {
    return b.missingToMinimum - a.missingToMinimum;
  }

  if (a.validCandidateCount !== b.validCandidateCount) {
    return a.validCandidateCount - b.validCandidateCount;
  }

  return a.submissionId.localeCompare(b.submissionId);
}

function buildPlanForSubmissions({
  submissions,
  reviewers,
  reviews,
  config,
  viewfieldsInstance,
  processedFields,
  getLinkedSubmissionId,
}) {
  logAssignEngine("buildPlanForSubmissions:ENTERED", {
    submissionCount: Array.isArray(submissions) ? submissions.length : 0,
    reviewerCount: Array.isArray(reviewers) ? reviewers.length : 0,
    reviewCount: Array.isArray(reviews) ? reviews.length : 0,
  });

  const recommendationResults = recommendForSubmissions({
    submissions,
    reviewers,
    reviews,
    config,
    viewfieldsInstance,
    processedFields,
    getLinkedSubmissionId,
    includeInvalidCandidates: false,
  });

  const limits = getLimits(config);
  const reviewerLoadMap = new Map();
  const submissionAssignmentMap = new Map();

  for (const reviewer of Array.isArray(reviewers) ? reviewers : []) {
    const reviewerId = getReviewerId(reviewer);
    if (!reviewerId) continue;
    reviewerLoadMap.set(
      reviewerId,
      Array.isArray(reviews)
        ? reviews.filter((review) => getReviewReviewerId(review) === reviewerId).length
        : 0
    );
  }

  const planRows = recommendationResults.map((result) => {
    const currentCount = result.currentSubmissionReviewCount;
    const minimumPerSubmission = limits.minimumPerSubmission;
    const maximumPerSubmission = limits.maximumPerSubmission;

    const missingToMinimum =
      minimumPerSubmission == null ? 0 : Math.max(0, minimumPerSubmission - currentCount);

    const availableCapacityToMaximum =
      maximumPerSubmission == null ? Number.MAX_SAFE_INTEGER : Math.max(0, maximumPerSubmission - currentCount);

    return {
      submissionId: result.submissionId,
      submission: result.submission,
      submissionAoE: result.submissionAoE,
      currentCount,
      missingToMinimum,
      availableCapacityToMaximum,
      validCandidateCount: result.validCandidates.length,
      rankedCandidates: result.validCandidates,
      assignedReviewerIds: [],
      unmetReasons: [],
    };
  });

  planRows.sort(comparePlanSubmissionPriority);

  for (const planRow of planRows) {
    const targetMinimumAdds = planRow.missingToMinimum;

    if (targetMinimumAdds <= 0) continue;

    for (const candidate of planRow.rankedCandidates) {
      if (planRow.assignedReviewerIds.length >= targetMinimumAdds) break;

      const reviewerId = candidate.reviewerId;
      const currentReviewerLoad = reviewerLoadMap.get(reviewerId) || 0;
      const maxReviewer = limits.maximumPerReviewer;

      if (planRow.assignedReviewerIds.includes(reviewerId)) continue;
      if (maxReviewer != null && currentReviewerLoad >= maxReviewer) continue;

      planRow.assignedReviewerIds.push(reviewerId);
      reviewerLoadMap.set(reviewerId, currentReviewerLoad + 1);

      if (!submissionAssignmentMap.has(planRow.submissionId)) {
        submissionAssignmentMap.set(planRow.submissionId, new Set());
      }
      submissionAssignmentMap.get(planRow.submissionId).add(reviewerId);
    }

    if (planRow.assignedReviewerIds.length < targetMinimumAdds) {
      planRow.unmetReasons.push("Unable to satisfy submission minimum with valid matching reviewers.");
    }
  }

  for (const planRow of planRows) {
    const maxAdds =
      planRow.availableCapacityToMaximum === Number.MAX_SAFE_INTEGER
        ? planRow.rankedCandidates.length
        : planRow.availableCapacityToMaximum;

    while (planRow.assignedReviewerIds.length < maxAdds) {
      let assignedAny = false;

      for (const candidate of planRow.rankedCandidates) {
        const reviewerId = candidate.reviewerId;
        const currentReviewerLoad = reviewerLoadMap.get(reviewerId) || 0;
        const maxReviewer = limits.maximumPerReviewer;

        if (planRow.assignedReviewerIds.includes(reviewerId)) continue;
        if (maxReviewer != null && currentReviewerLoad >= maxReviewer) continue;

        planRow.assignedReviewerIds.push(reviewerId);
        reviewerLoadMap.set(reviewerId, currentReviewerLoad + 1);
        assignedAny = true;
        break;
      }

      if (!assignedAny) break;
    }
  }

  const plan = planRows.map((row) => ({
    submissionId: row.submissionId,
    submission: row.submission,
    submissionAoE: row.submissionAoE,
    assignedReviewerIds: row.assignedReviewerIds,
    rankedCandidates: row.rankedCandidates,
    unmetReasons: row.unmetReasons,
    currentCount: row.currentCount,
  }));

  logAssignEngine("buildPlanForSubmissions:summary", {
    planLength: plan.length,
    limits,
    rows: plan.map((row) => ({
      submissionId: row.submissionId,
      assignedReviewerIds: row.assignedReviewerIds,
      unmetReasons: row.unmetReasons,
    })),
  });

  return {
    plan,
    limits,
  };
}

function validateSelection({
  submission,
  reviewerIds,
  reviewers,
  reviews,
  config,
  viewfieldsInstance,
  processedFields,
  getLinkedSubmissionId,
}) {
  logAssignEngine("validateSelection:ENTERED", {
    submissionId: getSubmissionId(submission),
    reviewerIds: Array.isArray(reviewerIds) ? reviewerIds : [],
    reviewerCount: Array.isArray(reviewers) ? reviewers.length : 0,
    reviewCount: Array.isArray(reviews) ? reviews.length : 0,
  });

  const recommendation = recommendForSubmission({
    submission,
    reviewers,
    reviews,
    config,
    viewfieldsInstance,
    processedFields,
    getLinkedSubmissionId,
    includeInvalidCandidates: true,
  });

  const selectedIds = uniqueStrings(Array.isArray(reviewerIds) ? reviewerIds.map(String) : []);
  const validSet = new Map(recommendation.validCandidates.map((candidate) => [candidate.reviewerId, candidate]));
  const invalidSet = new Map(recommendation.invalidCandidates.map((candidate) => [candidate.reviewerId, candidate]));

  const result = {
    submissionId: recommendation.submissionId,
    validSelections: selectedIds
      .filter((reviewerId) => validSet.has(reviewerId))
      .map((reviewerId) => validSet.get(reviewerId)),
    invalidSelections: selectedIds
      .filter((reviewerId) => !validSet.has(reviewerId))
      .map((reviewerId) => invalidSet.get(reviewerId) || { reviewerId, reasons: ["Reviewer is not currently assignable."] }),
  };

  logAssignEngine("validateSelection:summary", {
    submissionId: result.submissionId,
    validSelectionIds: result.validSelections.map((item) => item.reviewerId),
    invalidSelections: result.invalidSelections,
  });

  return result;
}

function validatePlan({
  plan,
  reviewers,
  reviews,
  config,
  viewfieldsInstance,
  processedFields,
  getLinkedSubmissionId,
}) {
  logAssignEngine("validatePlan:ENTERED", {
    rowCount: Array.isArray(plan) ? plan.length : 0,
    reviewerCount: Array.isArray(reviewers) ? reviewers.length : 0,
    reviewCount: Array.isArray(reviews) ? reviews.length : 0,
  });

  const rows = Array.isArray(plan) ? plan : [];

  const result = rows.map((row) =>
    validateSelection({
      submission: row.submission,
      reviewerIds: row.assignedReviewerIds,
      reviewers,
      reviews,
      config,
      viewfieldsInstance,
      processedFields,
      getLinkedSubmissionId,
    })
  );

  logAssignEngine("validatePlan:summary", {
    validatedRows: result.length,
    submissionIds: result.map((row) => row.submissionId),
  });

  return result;
}

const assignEngine = {
  recommendForSubmission,
  recommendForSubmissions,
  buildPlanForSubmissions,
  validateSelection,
  validatePlan,
};

export {
  recommendForSubmission,
  recommendForSubmissions,
  buildPlanForSubmissions,
  validateSelection,
  validatePlan,
};

export default assignEngine;
